import { createContext, useContext, useRef, useState } from "react";
import {
  getCurrentUser,
  login as loginAPI,
  register as registerAPI,
  logout as logoutAPI,
  updatePassword as updatePasswordAPI,
  setup as setupAPI,
} from "@/apis/auth";
import { getRegions } from "@/apis";

const TOKEN_KEY = "tenancy_access_token";

export const AuthContext = createContext(undefined);
export const TENANT_ADMIN = "TENANT_ADMIN";
export const SAAS_ADMIN = "SAAS_ADMIN";
export const TENANT_USER = "TENANT_USER";

export const AuthProviderComponent = ({ children }) => {
  const [user, setUser] = useState(null);
  const userCallback = useRef([]);
  const isFetchingUser = useRef(false);
  const loadingStatus = useRef({ eks: { isLoading: false, isError: false } });
  const mfaRef = useRef(null);
  const [eks, setEks] = useState(null);

  const setToken = (token) => {
    if (import.meta.env.DEV) {
      localStorage.setItem(TOKEN_KEY, token);
    }
  };

  const login = async ({ tenant, email, username, password, code, challenge }) => {
    let data;
    if (challenge) {
      data = await loginAPI({ code, session: mfaRef.current.session });
    } else {
      data = await loginAPI({ tenant, email, username, password });
    }
    if (data.token) {
      if (data.token.challenge) {
        mfaRef.current = data.token;
        return { success: true, ...data.token };
      } else {
        mfaRef.current = null;
        setToken(data.token);
        return { success: true };
      }
    } else if (data.error_code) {
      return { success: false, ...data };
    }
    return { success: false };
  };

  const logout = async () => {
    await logoutAPI();
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    if (location.pathname === "/login-sso") {
      return { success: true };
    } else {
      return { success: true, redirectTo: "/login" };
    }
  };

  const check = async () => {
    if (mfaRef.current) {
      return { authenticated: false, mfa: mfaRef.current };
    }
    const user = await getIdentity();
    if (user) {
      return { authenticated: true };
    } else {
      return {
        authenticated: false,
        logout: true,
        redirectTo: "/login",
      };
    }
  };

  const onError = async (error) => {
    if (error?.status === 401) {
      return {
        logout: true,
      };
    }
    return { error };
  };

  const getIdentity = async () => {
    // always try to get user from server, the token might be stored in base domain cookie
    /* if (!tokenRef.current) {
      return null;
    } */
    if (user) {
      return user;
    }
    if (isFetchingUser.current) {
      return new Promise((resolve) => {
        userCallback.current.push(resolve);
      });
    }
    isFetchingUser.current = true;
    const data = await getCurrentUser();
    if (data?.token) {
      setToken(data.token);
    }
    if (data && !eks && !loadingStatus.current.eks.isLoading) {
      loadingStatus.current.eks.isLoading = true;
      getRegions()
        .then((data) => {
          if (data.status === "OK") {
            setEks(data.value);
          } else {
            loadingStatus.current.eks.isError = true;
          }
          loadingStatus.current.eks.isLoading = false;
        })
        .catch(() => {
          loadingStatus.current.eks.isError = true;
          loadingStatus.current.eks.isLoading = false;
        });
    }
    if (data?.isAdminNest === true || data?.tenant == "__root__") {
      data.isSaaSAdmin = true;
      data.is = SAAS_ADMIN;
    } else if (data?.isAdmin === true) {
      data.isTenantAdmin = true;
      data.is = TENANT_ADMIN;
    } else if (data?.isAdmin === false) {
      data.is = TENANT_USER;
    }
    setUser(data);
    isFetchingUser.current = false;
    userCallback.current.forEach((callback) => callback(data));
    userCallback.current = [];
    return data;
  };

  const authProvider = {
    login,
    logout,
    check,
    onError,
    getIdentity,
    register: async ({ tenant, user, email, password, instance, domain_prefix, token }) => {
      const data = await setupAPI({ tenant, user, email, password, instance, domain_prefix, token });
      if (data.status === "OK" || data.status === "ok") {
        return {
          success: true,
          redirectTo: "/login?tenant=" + tenant ,
          successNotification: {
            message: "Setup success",
          },
        };
      } else {
        return {
          success: false,
          error: {
            message: "Setup failed",
            name: data.error.message,
          },
        };
      }
    },

    updatePassword: async ({ oldpassword, newpassword }) => {
      const data = await updatePasswordAPI({
        oldpassword,
        newpassword,
      });
      if (data.status === "OK") {
        return {
          success: true,
          successNotification: {
            message: "Update password successful",
            description: "You have successfully updated password.",
          },
        };
      } else {
        return {
          success: false,
          error: {
            message: "Update password",
            name: data.error.message,
          },
        };
      }
    },

    forgotPassword: async (params) => {
      if (params.email === "demo@demo.com") {
        //we can send email with reset password link here
        return {
          success: true,
        };
      }
      return {
        success: false,
        error: {
          message: "Forgot password failed",
          name: "Invalid email",
        },
      };
    },
    getPermissions: async () => {
      throw new Error("Not implemented");
    },
    user,
  };
  return (
    <AuthContext.Provider
      value={{
        authProvider,
        eks: { data: eks, ...loadingStatus.current.eks },
        user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within a AuthProvider");
  }
  return context;
};
